import { assets, type ImageAsset } from '@/assets';

/** A speech sound being practised, e.g. /r/. */
export interface TargetSound {
  /** As shown to children and parents. */
  label: string;
  /** IPA phoneme used for scoring. */
  phoneme: string;
}

/** One spelling unit of a word and the sound it makes, e.g. "ck" -> k. */
export interface WordSegment {
  letters: string;
  /** IPA phoneme (General American). */
  phoneme: string;
}

export interface PracticeWord {
  id: string;
  text: string;
  /** Letters aligned to sounds, in order; joined they spell `text`. */
  segments: readonly WordSegment[];
  /** Cue-card picture. */
  picture: ImageAsset;
  /**
   * An easier target with the same sound, for when the word is too hard: a consonant-vowel
   * syllable whose recordings the /ɹ/ classifier was validated on (rah, ray, ree, roh, roo).
   */
  syllable: { text: string; phonemes: readonly string[] };
  /** The target is itself a practice syllable (judged as one; no easier step below it). */
  kind?: 'word' | 'syllable';
}

export interface WordSet {
  sound: TargetSound;
  words: readonly PracticeWord[];
}

const pictures = assets.objects.targetWords;

/**
 * Practice syllables (all with clinician-rated recordings in PERCEPT-R): consonant-vowel for /ɹ/
 * before a vowel, vowel-/ɹ/ for vocalic /ɹ/.
 */
export const SYLLABLES = {
  rah: { text: 'rah', phonemes: ['ɹ', 'ɑ'] },
  ray: { text: 'ray', phonemes: ['ɹ', 'eɪ'] },
  ree: { text: 'ree', phonemes: ['ɹ', 'i'] },
  roh: { text: 'roh', phonemes: ['ɹ', 'oʊ'] },
  roo: { text: 'roo', phonemes: ['ɹ', 'u'] },
  ar: { text: 'ar', phonemes: ['ɑ', 'ɹ'] },
  or: { text: 'or', phonemes: ['ɔ', 'ɹ'] },
  air: { text: 'air', phonemes: ['ɛ', 'ɹ'] },
  ear: { text: 'ear', phonemes: ['ɪ', 'ɹ'] },
} as const;

/**
 * Builds a word from "letters:phoneme" pairs, e.g. word(picture, SYLLABLES.ray, 'r:ɹ', 'e:ɛ',
 * 'd:d').
 */
export function word(
  picture: ImageAsset,
  syllable: PracticeWord['syllable'],
  ...pairs: string[]
): PracticeWord {
  const segments = pairs.map((pair) => {
    const [letters = '', phoneme = ''] = pair.split(':');
    return { letters, phoneme };
  });
  const text = segments.map((s) => s.letters).join('');
  return { id: text, text, segments, picture, syllable };
}

/**
 * Initial /r/: words that start with the target sound. Chosen for coverage in the clinician-rated
 * recordings the scoring is trained and tested on (PERCEPT-R: each word recorded by 29-33
 * children, about 55-60 of them by children aged 6-8), so every word's scoring is well tested.
 */
export const initialR: WordSet = {
  sound: { label: '/r/', phoneme: 'ɹ' },
  words: [
    word(pictures.rocks, SYLLABLES.rah, 'r:ɹ', 'o:ɑ', 'ck:k'),
    word(pictures.rocket, SYLLABLES.rah, 'r:ɹ', 'o:ɑ', 'ck:k', 'e:ɪ', 't:t'),
    word(pictures.ring, SYLLABLES.ree, 'r:ɹ', 'i:ɪ', 'ng:ŋ'),
    word(pictures.robot, SYLLABLES.roh, 'r:ɹ', 'o:oʊ', 'b:b', 'o:ɑ', 't:t'),
    word(pictures.rhino, SYLLABLES.rah, 'rh:ɹ', 'i:aɪ', 'n:n', 'o:oʊ'),
    word(pictures.rabbit, SYLLABLES.rah, 'r:ɹ', 'a:æ', 'bb:b', 'i:ɪ', 't:t'),
    word(pictures.robin, SYLLABLES.rah, 'r:ɹ', 'o:ɑ', 'b:b', 'i:ɪ', 'n:n'),
    word(pictures.rope, SYLLABLES.roh, 'r:ɹ', 'o:oʊ', 'pe:p'),
  ],
};

/**
 * More initial /r/ words, used to test scoring with words that have real clinician-rated
 * recordings (held-out PERCEPT-R children). They have no pictures yet, so they show a placeholder
 * picture; the scanner uses them with `?mic=sim&words=rose,ring,...` (development and tests).
 */
export const testWordsR: readonly PracticeWord[] = [
  word(pictures.rocks, SYLLABLES.rah, 'r:ɹ', 'o:ɑ', 'ck:k'),
  word(pictures.rocks, SYLLABLES.roh, 'r:ɹ', 'o:oʊ', 'se:z'),
  word(pictures.rocks, SYLLABLES.ray, 'r:ɹ', 'i:ɪ', 'ng:ŋ'),
  word(pictures.rocks, SYLLABLES.rah, 'r:ɹ', 'u:ʌ', 'g:ɡ'),
  word(pictures.rocks, SYLLABLES.rah, 'r:ɹ', 'i:aɪ', 'ce:s'),
  word(pictures.rocks, SYLLABLES.rah, 'r:ɹ', 'i:aɪ', 'de:d'),
  word(pictures.rocks, SYLLABLES.ray, 'r:ɹ', 'a:eɪ', 'ke:k'),
  word(pictures.rocks, SYLLABLES.ray, 'r:ɹ', 'a:eɪ', 'ce:s'),
  word(pictures.rocks, SYLLABLES.rah, 'r:ɹ', 'o:ɑ', 'b:b'),
  word(pictures.rocks, SYLLABLES.rah, 'r:ɹ', 'u:ʌ', 'n:n'),
  word(pictures.rocks, SYLLABLES.rah, 'r:ɹ', 'a:æ', 'f:f', 't:t'),
  word(pictures.rocks, SYLLABLES.ree, 'r:ɹ', 'ea:i', 'ch:tʃ'),
  word(pictures.rocks, SYLLABLES.roo, 'r:ɹ', 'u:u', 'de:d'),
  word(pictures.rocks, SYLLABLES.rah, 'r:ɹ', 'o:ɑ', 'b:b', 'i:ɪ', 'n:n'),
];

/** The practice words with the given ids (practice words first, then test words). */
export function wordSetWith(ids: readonly string[]): WordSet {
  const all = [...initialR.words, ...testWordsR];
  const words = ids.flatMap((id) => all.find((w) => w.id === id) ?? []);
  return { sound: initialR.sound, words: words.length > 0 ? words : initialR.words };
}
